import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import { Plus, Pencil, Trash2, Search, RefreshCw, Building2, X, Eye } from 'lucide-react'

function CompanyModal({ company, onClose, onSaved }) {
  const isEdit = !!company
  const [name, setName] = useState(company?.name || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save(event) {
    event.preventDefault()
    setSaving(true)
    setError('')

    try {
      const payload = { name: name.trim() }
      if (!payload.name) {
        setError('Company name is required.')
        setSaving(false)
        return
      }

      if (isEdit) {
        await api.patch(`/companies/${company.id}`, payload)
        toast.success('Company updated')
      } else {
        await api.post('/companies/', payload)
        toast.success('Company created')
      }

      onSaved()
      onClose()
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Error saving company.'
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={event => event.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 size={18} />
            </div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{isEdit ? 'Edit Company' : 'New Company'}</h2>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <form onSubmit={save} className="modal-body">
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Company Name *</label>
            <input
              className="input"
              value={name}
              onChange={event => setName(event.target.value)}
              required
              placeholder="e.g. Acme Corp"
            />
          </div>

          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>}
        </form>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Company'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CompanyCard({ company, canManage, canDelete, canImpersonate, onEdit, onDelete, onImpersonate }) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: 'var(--bg-base)',
            border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: 'var(--text-muted)', flexShrink: 0,
          }}>
            <Building2 size={16} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{company.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>ID: {company.id}</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        Created: {new Date(company.created_at).toLocaleDateString()}
      </div>

      {(canManage || canImpersonate) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, paddingTop: 10 }}>
          {canImpersonate && (
            <button className="btn btn-ghost btn-icon btn-sm" title="View as Partner" onClick={onImpersonate}>
              <Eye size={14} />
            </button>
          )}
          {canManage && (
            <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={onEdit}>
              <Pencil size={14} />
            </button>
          )}
          {canDelete && (
            <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={onDelete}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function CompaniesPage() {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { user, isAdmin, startImpersonation } = useAuth()
  const canManage = isAdmin || user?.is_staff
  const canDelete = isAdmin || user?.is_superuser
  // Impersonation is superuser-only - deliberately narrower than isAdmin
  // (which also covers is_staff), since it's a scope override on a live
  // session, not routine admin CRUD.
  const canImpersonate = !!user?.is_superuser

  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(null)
  const [delId, setDelId] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const load = useCallback(async (search = '', p = 1, size = 10) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('page', String(p))
      params.set('limit', String(size))
      params.set('page_size', String(size))
      params.set('per_page', String(size))
      if (search) params.set('search', search)
      const res = await api.get(`/companies/?${params.toString()}`)
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.companies || res.data?.data || [])
      setData(items)
      setTotal(res.data?.count || items.length || 0)
    } catch (err) {
      toast.error('Failed to load companies')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(q, page, pageSize) }, [load, page, pageSize])

  const handleImpersonate = async (companyId) => {
    try {
      await startImpersonation(companyId)
      navigate('/partner-dashboard')
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Already impersonating a company. End that session first.')
      } else if (err.response?.status === 429) {
        toast.error('Too many impersonation attempts. Try again shortly.')
      } else {
        toast.error(err.response?.data?.message || 'Failed to start impersonation.')
      }
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/companies/${delId}`)
      toast.success('Company deleted')
      load(q, page, pageSize)
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Delete failed')
    }
    setDelId(null)
  }

  const totalPages = Math.ceil(total / pageSize) || 1

  return (
    <div className="fade-in page-shell">
      <section className="page-header" style={{ marginBottom: 32 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 16 : 0,
        }}>
          <div>
            <h1 style={{ margin: 0 }}>Companies</h1>
            <p style={{ marginTop: 4 }}>Root entities that group Affiliates and Buyers.</p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-ghost" onClick={() => load(q, page, pageSize)}>
              <RefreshCw size={16} />
            </button>
            {canManage && (
              <button className="btn btn-primary" style={{ flex: isMobile ? 1 : undefined, justifyContent: 'center' }} onClick={() => setModal('create')}>
                <Plus size={18} style={{ marginRight: 6 }} /> New Company
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="card" style={{ padding: 16, marginBottom: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200, margin: 0 }}>
          <Search size={14} className="search-icon" />
          <input
            className="input"
            placeholder="Search by name..."
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(q, 1, pageSize)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => { setPage(1); load(q, 1, pageSize); }}>Search</button>
      </section>

      {loading && data.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
        </div>
      ) : isMobile ? (
        data.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No companies found.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.map(c => (
              <CompanyCard
                key={c.id}
                company={c}
                canManage={canManage}
                canDelete={canDelete}
                canImpersonate={canImpersonate}
                onEdit={() => setModal(c)}
                onDelete={() => setDelId(c.id)}
                onImpersonate={() => handleImpersonate(c.id)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 80 }}>ID</th>
                <th>Name</th>
                <th>Created At</th>
                {(canManage || canImpersonate) && <th style={{ width: 130, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={(canManage || canImpersonate) ? 4 : 3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No companies found.</td></tr>
              ) : (
                data.map(c => (
                  <tr key={c.id}>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.id}</td>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(c.created_at).toLocaleString()}</td>
                    {(canManage || canImpersonate) && (
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {canImpersonate && (
                            <button className="btn btn-ghost btn-icon btn-sm" title="View as Partner" onClick={() => handleImpersonate(c.id)}>
                              <Eye size={13} />
                            </button>
                          )}
                          {canManage && (
                            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(c)}>
                              <Pencil size={13} />
                            </button>
                          )}
                          {canDelete && (
                            <button className="btn btn-danger btn-icon btn-sm" onClick={() => setDelId(c.id)}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '12px 16px', background: 'var(--bg-raised)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Showing {data.length} of {total} companies (Page {page} of {totalPages})
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            className="select"
            style={{ width: 110, height: 32, fontSize: 12 }}
            value={pageSize}
            onChange={e => {
              const newSize = Number(e.target.value)
              setPageSize(newSize)
              setPage(1)
            }}
          >
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
            <option value="500">500 / page</option>
          </select>
          <button
            className="btn btn-ghost btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {(modal === 'create' || (modal && modal.id)) && (
        <CompanyModal
          company={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(q); }}
        />
      )}

      {delId && (
        <div className="modal-overlay" onClick={() => setDelId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Delete Company</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>Are you sure you want to delete this company? This will cascade and delete all associated affiliates and buyers.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setDelId(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
